import { useEffect, useCallback, useRef } from 'react';
import IconifyIcon from '../IconifyIcon';
import SimplebarReactClient from '../SimplebarReactClient';
import { DropdownItem, Row, Spinner } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/api.service';
import { useState } from 'react';

// Color map for notification types
const colorMap = {
  primary: { bg: 'bg-soft-primary', text: 'text-primary' },
  success: { bg: 'bg-soft-success', text: 'text-success' },
  warning: { bg: 'bg-soft-warning', text: 'text-warning' },
  danger: { bg: 'bg-soft-danger', text: 'text-danger' },
  info: { bg: 'bg-soft-info', text: 'text-info' }
};

// Get read notifications from localStorage
const getReadNotifications = () => {
  try {
    const read = localStorage.getItem('readNotifications');
    return read ? JSON.parse(read) : [];
  } catch {
    return [];
  }
};

// Save read notification to localStorage
const markAsRead = (notificationId) => {
  try {
    const read = getReadNotifications();
    if (!read.includes(notificationId)) {
      read.push(notificationId);
      localStorage.setItem('readNotifications', JSON.stringify(read));
    }
  } catch (e) {
    console.error('Error saving read notification:', e);
  }
};

const NotificationItem = ({ notification, onClick }) => {
  const colors = colorMap[notification.color] || colorMap.primary;
  
  return (
    <DropdownItem 
      className="py-3 border-bottom text-wrap notification-item"
      onClick={() => onClick(notification)}
      style={{ cursor: 'pointer' }}
    >
      <div className="d-flex align-items-start">
        <div className="flex-shrink-0">
          <div className={`avatar-sm me-2 ${colors.bg} rounded-circle d-flex align-items-center justify-content-center`}>
            <IconifyIcon icon={notification.icon} className={`fs-18 ${colors.text}`} />
          </div>
        </div>
        <div className="flex-grow-1 ms-2">
          <p className="mb-0 fw-semibold text-dark" style={{ fontSize: '13px' }}>{notification.title}</p>
          <p className="mb-0 text-muted" style={{ fontSize: '12px' }}>{notification.message}</p>
          <small className="text-muted" style={{ fontSize: '11px' }}>
            <IconifyIcon icon="bx:time-five" className="me-1" style={{ fontSize: '11px' }} />
            {notification.time}
          </small>
        </div>
      </div>
    </DropdownItem>
  );
};

const Notifications = ({ isOpen, onToggle, onClose }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const dropdownRef = useRef(null);
  const touchStartY = useRef(0);

  // Handle touch swipe down to close
  const handleTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };

  const handleTouchMove = (e) => {
    const touchY = e.touches[0].clientY;
    const diff = touchY - touchStartY.current;
    if (diff > 100) { // Swipe down 100px to close
      onClose();
    }
  };

  // Handle toggle
  const handleToggle = (nextShow) => {
    onToggle(nextShow);
  };

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const response = await apiService.get('/notifications');
      if (response.success) {
        const allNotifications = response.data.notifications || [];
        const readIds = getReadNotifications();
        
        // Filter out read notifications
        const unreadNotifications = allNotifications.filter(
          n => !readIds.includes(n.id)
        );
        
        setNotifications(unreadNotifications);
        setUnreadCount(unreadNotifications.length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount and every 30 seconds
  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // Refetch when dropdown opens
  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const handleNotificationClick = (notification) => {
    // Mark as read
    markAsRead(notification.id);
    
    // Remove from current list
    setNotifications(prev => prev.filter(n => n.id !== notification.id));
    setUnreadCount(prev => Math.max(0, prev - 1));
    
    // Close dropdown and navigate
    onClose();
    if (notification.link) {
      navigate(notification.link);
    }
  };

  const handleClearAll = () => {
    // Mark all as read
    notifications.forEach(n => markAsRead(n.id));
    setNotifications([]);
    setUnreadCount(0);
  };

  // Handle button click to toggle
  const handleButtonClick = (e) => {
    e.stopPropagation();
    if (isOpen) {
      onClose();
    } else {
      onToggle(true);
    }
  };

  return (
    <div className="topbar-item position-relative">
      <button
        type="button"
        className={`topbar-button position-relative ${isOpen ? 'active' : ''}`}
        onClick={handleButtonClick}
      >
        <IconifyIcon icon="solar:bell-bing-bold-duotone" style={{ fontSize: '22px' }} />
        {unreadCount > 0 && (
          <span 
            className="position-absolute topbar-badge badge bg-danger rounded-pill" 
            style={{ 
              zIndex: 9999, 
              top: '-2px', 
              right: '0px',
              fontSize: '10px',
              minWidth: '18px',
              height: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      
      {isOpen && (
        <>
          {/* Backdrop for closing */}
          <div 
            className="notifications-backdrop"
            onClick={onClose}
            onTouchEnd={onClose}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0,0,0,0.4)',
              zIndex: 99998
            }}
          />
          
          {/* Dropdown Menu */}
          <div 
            className="notifications-dropdown-menu show"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
          >
            {/* Header */}
            <div className="notifications-header p-3 border-bottom">
              <Row className="align-items-center">
                <div className="col">
                  <h6 className="m-0 fs-15 fw-semibold">
                    <IconifyIcon icon="solar:bell-bing-bold-duotone" className="me-2" style={{ color: '#c5a05e' }} />
                    Notifications
                  </h6>
                </div>
                <div className="col-auto">
                  {notifications.length > 0 && (
                    <button 
                      onClick={handleClearAll}
                      className="btn btn-link p-0 text-muted text-decoration-none"
                      style={{ fontSize: '12px' }}
                    >
                      Tout effacer
                    </button>
                  )}
                </div>
              </Row>
            </div>

            {/* Notifications List */}
            <SimplebarReactClient className="notifications-list-container">
              {loading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" variant="primary" />
                  <p className="mb-0 mt-2 text-muted" style={{ fontSize: '12px' }}>Chargement...</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="text-center py-4">
                  <IconifyIcon icon="solar:bell-off-bold-duotone" className="text-muted" style={{ fontSize: '40px' }} />
                  <p className="mb-0 mt-2 text-muted" style={{ fontSize: '13px' }}>Aucune notification</p>
                </div>
              ) : (
                notifications.map((notification, idx) => (
                  <NotificationItem 
                    key={notification.id || idx} 
                    notification={notification}
                    onClick={handleNotificationClick}
                  />
                ))
              )}
            </SimplebarReactClient>

            {/* Footer - fixed at bottom */}
            <div className="notifications-footer">
              <Link 
                to="/admin/notifications" 
                onClick={onClose}
                className="notifications-view-all-btn"
              >
                Voir toutes les notifications
                <IconifyIcon icon="bx:right-arrow-alt" className="ms-1" />
              </Link>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default Notifications;
